import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Smartphone, Chrome, Info, Sparkles, Share2, Download, BellRing } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BusinessInfoCard } from "@/components/business-info-block";

export const Route = createFileRoute("/user-manual")({
  head: () => ({
    meta: [
      { title: "Hướng Dẫn Cài Đặt & Sử Dụng - Bói Đê App" },
      {
        name: "description",
        content:
          "Hướng dẫn cài đặt ứng dụng Bói Đê trên iOS và Android, gieo quẻ Kinh Dịch, lập lá số Tử Vi và quản lý tài khoản.",
      },
    ],
  }),
  component: UserManualPage,
});

function UserManualPage() {
  return (
    <main className="flex-1 min-h-0 overflow-y-auto px-4 py-6 pb-24 md:px-8 md:py-10 md:pb-10 bg-background">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-primary font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Về trang chủ
        </Link>

        {/* Title Header Card */}
        <section className="rounded-3xl border border-gold-soft bg-card/70 p-6 shadow-mystic backdrop-blur md:p-8">
          <span className="font-sans text-[11px] tracking-[0.18em] font-semibold text-primary uppercase">
            HƯỚNG DẪN CÀI ĐẶT
          </span>
          <h1 className="mt-3 font-serif-soft text-3xl text-foreground md:text-4xl">
            Cài đặt & Sử dụng Bói Đê
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
            Cài đặt ứng dụng Bói Đê trực tiếp từ trình duyệt điện thoại (PWA) giúp trải nghiệm mượt mà hơn, tiết kiệm dung lượng và tự động cập nhật vận hạn hàng ngày qua thông báo.
          </p>
        </section>

        {/* PWA Installation Instructions Section */}
        <section className="rounded-3xl border border-gold-soft/50 bg-card/50 p-5 md:p-7 backdrop-blur space-y-6">
          <div className="flex items-center gap-2.5 pb-3 border-b border-gold-soft/20">
            <Smartphone className="h-5.5 w-5.5 text-gold" />
            <h2 className="font-serif-soft text-xl text-gold-gradient">Cài đặt App trên điện thoại</h2>
          </div>

          <Tabs defaultValue="ios" className="w-full space-y-6">
            <TabsList className="grid w-full grid-cols-2 h-10 rounded-xl bg-muted p-1">
              <TabsTrigger
                value="ios"
                className="rounded-lg h-full text-xs font-semibold font-serif-soft whitespace-nowrap"
              >
                 iOS
              </TabsTrigger>
              <TabsTrigger
                value="android"
                className="rounded-lg h-full text-xs font-semibold font-serif-soft whitespace-nowrap"
              >
                Android
              </TabsTrigger>
            </TabsList>

            {/* iOS Instructions with Screenshots */}
            <TabsContent value="ios" className="space-y-6 outline-none">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 1 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold font-serif-soft">
                    1
                  </div>
                  <h3 className="font-serif-soft text-sm font-semibold text-foreground">Bước 1: Mở Trình Đơn</h3>
                  <p className="text-xs text-muted-foreground px-4 leading-5">
                    Bấm biểu tượng <strong>Chia sẻ</strong> trên thanh công cụ Safari, hoặc bấm nút <strong>ba chấm (...)</strong> trên Chrome rồi chọn <strong>Chia sẻ</strong>.
                  </p>
                  <div className="relative mt-2 border border-gold-soft/20 rounded-2xl overflow-hidden shadow-mystic w-full flex justify-center">
                    <img
                      src="/guide/step-1.webp"
                      alt="Bước 1: Mở trình đơn Chia sẻ"
                      className="w-full h-auto max-w-[340px] md:max-w-none object-contain"
                    />
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold font-serif-soft">
                    2
                  </div>
                  <h3 className="font-serif-soft text-sm font-semibold text-foreground">Bước 2: Xem Bảng Chia Sẻ</h3>
                  <p className="text-xs text-muted-foreground px-4 leading-5">
                    Hệ thống sẽ hiển thị bảng các tuỳ chọn tương tác và chia sẻ của thiết bị iOS.
                  </p>
                  <div className="relative mt-2 border border-gold-soft/20 rounded-2xl overflow-hidden shadow-mystic w-full flex justify-center">
                    <img
                      src="/guide/step-2.webp"
                      alt="Bước 2: Bảng chia sẻ hệ thống"
                      className="w-full h-auto max-w-[340px] md:max-w-none object-contain"
                    />
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold font-serif-soft">
                    3
                  </div>
                  <h3 className="font-serif-soft text-sm font-semibold text-foreground">Bước 3: Thêm vào MH chính</h3>
                  <p className="text-xs text-muted-foreground px-4 leading-5">
                    Cuộn xuống bên dưới trong bảng chia sẻ hệ thống và nhấn vào <strong>Thêm vào Màn hình chính</strong>.
                  </p>
                  <div className="relative mt-2 border border-gold-soft/20 rounded-2xl overflow-hidden shadow-mystic w-full flex justify-center">
                    <img
                      src="/guide/step-3.webp"
                      alt="Bước 3: Thêm vào Màn hình chính"
                      className="w-full h-auto max-w-[340px] md:max-w-none object-contain"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Android Instructions */}
            <TabsContent value="android" className="space-y-5 outline-none">
              <div className="space-y-4">
                {/* Method 1 */}
                <div className="rounded-xl border border-gold-soft/20 bg-[#FCFAF7] dark:bg-[#1D1410] p-4 flex gap-4 items-start">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Download className="h-4 w-4" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-serif-soft text-sm font-semibold text-foreground">Cách 1: Cài đặt trực tiếp (Khuyên dùng)</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Khi truy cập trang web bằng Chrome trên Android, bạn sẽ thấy thông báo <strong>"Thêm Bói Đê vào Màn hình chính"</strong> trượt lên ở phía dưới. Chỉ cần bấm vào thông báo này để bắt đầu cài đặt tự động.
                    </p>
                  </div>
                </div>

                {/* Method 2 */}
                <div className="rounded-xl border border-gold-soft/20 bg-[#FCFAF7] dark:bg-[#1D1410] p-4 flex gap-4 items-start">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Chrome className="h-4 w-4" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-serif-soft text-sm font-semibold text-foreground">Cách 2: Cài đặt qua menu Chrome</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Nếu không thấy thông báo tự động, bấm vào biểu tượng <strong>ba dấu chấm dọc (⋮)</strong> ở góc trên bên phải thanh công cụ Chrome, chọn <strong>Cài đặt ứng dụng</strong> (hoặc <strong>Thêm vào Màn hình chính</strong>) và nhấn Xác nhận.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </section>

        {/* General App Manual Sections */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <Sparkles className="h-4.5 w-4.5 text-gold" />
            <h2 className="font-serif-soft text-base font-bold text-foreground">Hướng dẫn sử dụng các tính năng</h2>
          </div>

          {/* Step 1: Login */}
          <article className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
            <h2 className="font-serif-soft text-lg text-gold-gradient">1. Đăng nhập tài khoản</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>Truy cập Bói Đê, nhấn nút Đăng nhập ở góc trên và sử dụng tài khoản đã đăng ký hoặc đăng nhập nhanh thông qua phương thức liên kết được hỗ trợ.</p>
              <p>Sau khi đăng nhập thành công, bạn có thể dễ dàng kiểm tra số dư, lịch sử các lần xin quẻ và kích hoạt đầy đủ các chức năng cá nhân hoá lá số.</p>
            </div>
          </article>

          {/* Step 2: I-Ching */}
          <article className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
            <h2 className="font-serif-soft text-lg text-gold-gradient">2. Gieo quẻ Kinh Dịch</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>Vào chức năng <strong>Kinh Dịch</strong> trên menu, nhập việc cần xem (nhập rõ ràng bối cảnh, câu hỏi sẽ giúp kết quả chính xác hơn), chọn một vị thầy luận giải rồi tiến hành gieo quẻ.</p>
              <p>Hệ thống sẽ tái hiện nghi thức gieo 3 đồng xu cổ. Sau khi quẻ lập thành, bạn có thể gửi câu hỏi chat để tiếp tục tham vấn chi tiết về thời cơ, hướng giải quyết hoặc dự báo tương lai.</p>
            </div>
          </article>

          {/* Step 3: Zi Wei & Ba Zi */}
          <article className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
            <h2 className="font-serif-soft text-lg text-gold-gradient">3. Lập lá số Tử Vi & Bát Tự</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>Vào mục <strong>Tử Vi</strong> hoặc <strong>Bát Tự</strong>, điền chính xác họ tên, ngày tháng năm sinh (Dương lịch hoặc Âm lịch) và đặc biệt là giờ sinh của bạn.</p>
              <p>Hệ thống sẽ lập tức an sao lá số 12 cung hoặc phân tích cấu trúc 4 trụ ngũ hành sinh khắc. Bạn có thể mở khóa các phần luận giải chi tiết và chat trực tiếp với AI để hỏi sâu về cung Tài Bạch, Quan Lộc, Phu Thê hay đại vận sắp tới.</p>
            </div>
          </article>

          {/* Step 4: Token payment */}
          <article className="rounded-2xl border border-gold-soft/50 bg-card/60 p-5 backdrop-blur md:p-6">
            <h2 className="font-serif-soft text-lg text-gold-gradient">4. Quản lý số dư & Nạp tiền</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>Mỗi lượt luận giải quẻ hoặc lá số chuyên sâu sẽ được trừ trực tiếp từ số dư. Bạn có thể vào mục <strong>Nạp tiền</strong> để quét mã QR thanh toán nhanh chóng.</p>
              <p>Sau khi hệ thống ghi nhận giao dịch thành công, số dư sẽ được cộng tự động. Bạn có thể theo dõi chi tiết biến động số dư và lịch sử thanh toán tại trang Biến động số dư.</p>
            </div>
          </article>
        </section>

        {/* Footer Info */}
        <BusinessInfoCard />
      </div>
    </main>
  );
}
